import { useState, useEffect, useRef, useCallback } from "react";
import { marketDashboardData } from "@/mocks/homepageData";

interface PairConfig {
  pair: string;
  calc: (rates: Record<string, number>) => number | null;
  baseSpread: number;
  decimals: number;
}

const FOREX_PAIRS: PairConfig[] = [
  {
    pair: "EUR/USD",
    calc: (r) => r.USD ?? null,
    baseSpread: 0.2,
    decimals: 5,
  },
  {
    pair: "GBP/USD",
    calc: (r) => {
      const eurUsd = r.USD;
      const eurGbp = r.GBP;
      if (!eurUsd || !eurGbp || eurGbp === 0) return null;
      return eurUsd / eurGbp;
    },
    baseSpread: 0.9,
    decimals: 5,
  },
  {
    pair: "USD/JPY",
    calc: (r) => {
      const eurJpy = r.JPY;
      const eurUsd = r.USD;
      if (!eurJpy || !eurUsd || eurUsd === 0) return null;
      return eurJpy / eurUsd;
    },
    baseSpread: 0.7,
    decimals: 3,
  },
  {
    pair: "AUD/USD",
    calc: (r) => {
      const eurUsd = r.USD;
      const eurAud = r.AUD;
      if (!eurUsd || !eurAud || eurAud === 0) return null;
      return eurUsd / eurAud;
    },
    baseSpread: 0.8,
    decimals: 5,
  },
  {
    pair: "USD/CHF",
    calc: (r) => {
      const eurChf = r.CHF;
      const eurUsd = r.USD;
      if (!eurChf || !eurUsd || eurUsd === 0) return null;
      return eurChf / eurUsd;
    },
    baseSpread: 0.8,
    decimals: 5,
  },
  {
    pair: "USD/CAD",
    calc: (r) => {
      const eurCad = r.CAD;
      const eurUsd = r.USD;
      if (!eurCad || !eurUsd || eurUsd === 0) return null;
      return eurCad / eurUsd;
    },
    baseSpread: 1.0,
    decimals: 5,
  },
];

interface ExtraPairConfig {
  pair: string;
  baseSpread: number;
  decimals: number;
  defaultPrice: number;
  jitterPct: number;
}

const EXTRA_PAIRS: ExtraPairConfig[] = [
  { pair: "XAU/USD", baseSpread: 1.8, decimals: 2, defaultPrice: 2334.61, jitterPct: 0.0008 },
  { pair: "BTC/USD", baseSpread: 20.0, decimals: 2, defaultPrice: 67235.52, jitterPct: 0.002 },
];

export interface ForexItem {
  pair: string;
  price: number;
  spread: number;
  change: number;
  trend: string;
  volume: string;
  bars: number[];
}

function getVolumeCategory(price: number, prevPrice: number): string {
  if (prevPrice <= 0 || price <= 0) return "Medium";
  const movePct = Math.abs((price - prevPrice) / prevPrice) * 100;
  if (movePct > 0.08) return "Very High";
  if (movePct > 0.03) return "High";
  return "Medium";
}

function mapMockToForexItem(): ForexItem[] {
  return marketDashboardData.map((item) => ({
    pair: item.pair,
    price: item.price,
    spread: item.spread,
    change: item.change,
    trend: item.trend,
    volume: item.volume,
    bars: item.bars,
  }));
}

function fetchWithTimeout(url: string, timeoutMs: number): Promise<Response> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error("Request timeout")), timeoutMs);
    fetch(url)
      .then((res) => {
        clearTimeout(timer);
        resolve(res);
      })
      .catch((err) => {
        clearTimeout(timer);
        reject(err);
      });
  });
}

function fetchBTCPrice(): Promise<number | null> {
  return fetchWithTimeout(
    "https://api.coingecko.com/api/v3/simple/price?ids=bitcoin&vs_currencies=usd",
    6000,
  )
    .then((res) => {
      if (!res.ok) throw new Error(`CoinGecko HTTP ${res.status}`);
      return res.json();
    })
    .then((json: { bitcoin?: { usd?: number } }) => {
      const price = json?.bitcoin?.usd;
      return typeof price === "number" && price > 0 ? price : null;
    })
    .catch(() => null);
}

export function useForexData(pollInterval = 60000) {
  const [data, setData] = useState<ForexItem[]>(() => mapMockToForexItem());
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isLive, setIsLive] = useState(false);

  const priceHistoryRef = useRef<Record<string, number[]>>({});
  const prevPricesRef = useRef<Record<string, number>>({});
  const prevDataRef = useRef<ForexItem[]>([]);
  const consecutiveFailures = useRef(0);

  const fetchRates = useCallback(async () => {
    const urls = [
      "https://api.frankfurter.dev/v1/latest?from=EUR&to=USD,GBP,JPY,AUD,CHF,CAD",
      "https://open.er-api.com/v6/latest/EUR",
    ];

    let lastErr: Error | null = null;

    for (const url of urls) {
      try {
        const res = await fetchWithTimeout(url, 8000);

        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }

        const json = await res.json();

        // Normalise both Frankfurter (v1) and open.er-api (v6) shapes
        const rates: Record<string, number> = json.rates ?? {};

        if (!rates || Object.keys(rates).length === 0) {
          throw new Error("Empty rates response");
        }

        const maxBars = 25;

        const newData: ForexItem[] = FOREX_PAIRS.map((cfg) => {
          const { pair, calc, baseSpread, decimals } = cfg;
          const rawRate = calc(rates);

          let price: number;
          if (rawRate && rawRate > 0) {
            const microJitter = 1 + (Math.random() - 0.5) * 0.00004;
            price = +(rawRate * microJitter).toFixed(decimals);
          } else {
            const fallback = prevDataRef.current.find((d) => d.pair === pair);
            price = fallback?.price ?? 0;
          }

          if (!priceHistoryRef.current[pair]) {
            // Seed history from previous data (mock bars) so sparkline never disappears
            const prevItem = prevDataRef.current.find((d) => d.pair === pair);
            priceHistoryRef.current[pair] = prevItem?.bars?.length ? [...prevItem.bars] : [];
          }
          const history = priceHistoryRef.current[pair];
          history.push(price);
          if (history.length > maxBars) history.shift();

          const prevPrice = prevPricesRef.current[pair];
          let change = 0;
          if (prevPrice && prevPrice > 0) {
            change = +(((price - prevPrice) / prevPrice) * 100).toFixed(2);
          }
          const changeClamped = Math.max(-99, Math.min(99, change));
          const trend = changeClamped >= 0 ? "up" : "down";

          const spreadJitter = (Math.random() - 0.5) * baseSpread * 0.3;
          const spread = Math.max(0.05, +(baseSpread + spreadJitter).toFixed(1));

          const volume = getVolumeCategory(price, prevPrice ?? price);

          prevPricesRef.current[pair] = price;

          return {
            pair,
            price,
            spread,
            change: changeClamped,
            trend,
            volume,
            bars: [...history],
          };
        });

        // Append XAU/USD and BTC/USD with realistic jitter (forex APIs don't cover metals/crypto)
        for (const extra of EXTRA_PAIRS) {
          const prev = prevDataRef.current.find((d) => d.pair === extra.pair);
          const basePrice = prev?.price ?? extra.defaultPrice;
          const jitter = 1 + (Math.random() - 0.5) * 2 * extra.jitterPct;
          const price = +(basePrice * jitter).toFixed(extra.decimals);

          if (!priceHistoryRef.current[extra.pair]) {
            // Seed history from previous data (mock bars) so sparkline never disappears
            const prevExtra = prevDataRef.current.find((d) => d.pair === extra.pair);
            priceHistoryRef.current[extra.pair] = prevExtra?.bars?.length ? [...prevExtra.bars] : [];
          }
          const history = priceHistoryRef.current[extra.pair];
          history.push(price);
          if (history.length > maxBars) history.shift();

          const prevPrice = prevPricesRef.current[extra.pair];
          let change = 0;
          if (prevPrice && prevPrice > 0) {
            change = +(((price - prevPrice) / prevPrice) * 100).toFixed(2);
          }
          const changeClamped = Math.max(-99, Math.min(99, change));
          const trend = changeClamped >= 0 ? "up" : "down";

          const spreadJitter = (Math.random() - 0.5) * extra.baseSpread * 0.3;
          const spread = Math.max(0.05, +(extra.baseSpread + spreadJitter).toFixed(1));
          const volume = getVolumeCategory(price, prevPrice ?? price);

          prevPricesRef.current[extra.pair] = price;

          newData.push({
            pair: extra.pair,
            price,
            spread,
            change: changeClamped,
            trend,
            volume,
            bars: [...history],
          });
        }

        prevDataRef.current = newData;
        setData(newData);
        setLastUpdate(new Date());
        setIsLoading(false);
        setIsLive(true);
        setError(null);
        consecutiveFailures.current = 0;

        // Fetch BTC from CoinGecko in the background (non-blocking, enriches BTC price)
        fetchBTCPrice()
          .then((btcPrice) => {
            if (btcPrice !== null) {
              setData((prev) =>
                prev.map((item) => {
                  if (item.pair !== "BTC/USD") return item;
                  const prevP = item.price;
                  const chg = prevP > 0 ? +(((btcPrice - prevP) / prevP) * 100).toFixed(2) : 0;
                  const newHistory = [...item.bars];
                  newHistory.push(btcPrice);
                  if (newHistory.length > maxBars) newHistory.shift();
                  return {
                    ...item,
                    price: btcPrice,
                    change: Math.max(-99, Math.min(99, chg)),
                    trend: chg >= 0 ? "up" : "down",
                    volume: getVolumeCategory(btcPrice, prevP),
                    bars: newHistory,
                  };
                }),
              );
            }
          })
          .catch(() => {
            // CoinGecko failed silently; BTC still updating via jitter above
          });

        return; // success — stop trying further URLs
      } catch (err: unknown) {
        lastErr = err instanceof Error ? err : new Error("Unknown error");
        // try next URL
      }
    }

    // All URLs failed
    consecutiveFailures.current++;
    const msg = lastErr?.message ?? "Unknown error";
    console.error("Forex fetch error (all sources):", msg);

    if (consecutiveFailures.current <= 3) {
      setError(`Connecting to market data... (attempt ${consecutiveFailures.current}/3)`);
    } else {
      setError("Live data temporarily unavailable. Showing cached data.");
      setIsLoading(false);
      setIsLive(false);
    }
  }, []);

  useEffect(() => {
    fetchRates();
    const interval = setInterval(fetchRates, pollInterval);
    return () => clearInterval(interval);
  }, [fetchRates, pollInterval]);

  return { data, lastUpdate, isLoading, error, isLive };
}